/**
 * TSV Discovery Collection Reward — redeem page handler
 *
 * Lives in assets/tsv-redeem.js, loaded by sections/tsv-redeem-form.liquid.
 * Calls /apps/redeem/check (Shopify App Proxy → discovery-rewards worker).
 *
 * The worker always returns 200 with a `success` boolean and a `status` string
 * (see worker source for the full status vocabulary). This script swaps the
 * DOM between idle / loading / success / error states based on the response.
 */
(function () {
  'use strict';

  function init(root) {
    if (!root || root.__tsvRedeemInitDone) return;
    root.__tsvRedeemInitDone = true;

    var form = root.querySelector('[data-tsv-redeem-form]');
    var btn = root.querySelector('[data-tsv-submit]');
    var errorBox = root.querySelector('[data-tsv-error]');
    var successPanel = root.querySelector('[data-tsv-success]');
    var trust = root.querySelector('[data-tsv-trust]');
    if (!form || !btn) return;

    var endpoint = root.getAttribute('data-endpoint') || '/apps/redeem/check';
    var shopUrl = root.getAttribute('data-shop-url') || '/pages/discovery-collection-full-bottles';
    var helpEndpoint = root.getAttribute('data-help-endpoint') || '/apps/redeem/help';
    var waitlistEndpoint = root.getAttribute('data-waitlist-endpoint') || '/apps/redeem/waitlist';

    initHelpPanel(root, helpEndpoint);
    initOrderHelpPopover(root);
    initSignedInToggle(root);
    initHowStrip(root);
    // URL param prefill runs after the submit handler is wired below, so
    // it can trigger the form submission cleanly.

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      hideError();
      setLoading(true);

      var data = new FormData(form);
      var orderNumber = String(data.get('order_number') || '').trim();
      var email = String(data.get('email') || '').trim().toLowerCase();
      var newsletterOptIn = !!data.get('newsletter_opt_in');

      if (!orderNumber) { setLoading(false); return showError('Please enter your order number.'); }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setLoading(false);
        return showError('Please enter a valid email address.');
      }

      // Fire-and-forget newsletter opt-in (Task 8). Runs in parallel with the
      // redeem call so it never blocks the main flow.
      if (newsletterOptIn) {
        postNewsletterOptIn(waitlistEndpoint, email);
      }

      var url = endpoint + '?order_number=' + encodeURIComponent(orderNumber) +
                '&email=' + encodeURIComponent(email);

      fetch(url, { method: 'GET', credentials: 'same-origin', cache: 'no-store' })
        .then(function (r) { return r.json(); })
        .then(function (data) { handleResponse(data, email); })
        .catch(function (err) {
          setLoading(false);
          // Network / fetch failure (Task 6).
          showError(
            'Something went wrong on our end. Refresh the page and try again. ' +
            'If it keeps happening, <a href="mailto:help@thescentvault.ie">contact us</a>.'
          );
        });
    });

    function handleResponse(data, email) {
      setLoading(false);
      if (!data || typeof data !== 'object') {
        return showError(
          'Something went wrong on our end. Refresh the page and try again. ' +
          'If it keeps happening, <a href="mailto:help@thescentvault.ie">contact us</a>.'
        );
      }

      // Error / non-success branch — map worker status codes to friendly copy
      // (Task 6). Worker statuses (from discovery-rewards.js): invalid_input,
      // rate_limited, lookup_failed, order_not_found, cancelled, refunded,
      // not_paid, not_eligible, window_closed, create_failed, server_error,
      // already_used, unauthorized.
      if (!data.success) {
        return showError(buildErrorMessage(data));
      }

      // Success branch. The worker may return:
      //   status: 'new_code_created' — first-time redemption
      //   status: 'existing_code_returned' — already redeemed (Task 7),
      //     rewards[].reused = true, original code + expires_at carried back
      //   status: 'already_used' — used (worker returns success:false, handled above)
      renderSuccess(data, email);
    }

    // Build a friendly error banner from the worker's status/message (Task 6).
    function buildErrorMessage(data) {
      var status = String(data.status || '').toLowerCase();
      var contact = ' <a href="mailto:help@thescentvault.ie">Contact us</a>.';
      switch (status) {
        case 'order_not_found':
          return 'We couldn\'t match that order number to that email. ' +
                 'Double-check your order confirmation email, and try without the # symbol. ' +
                 'Still stuck? <a href="mailto:help@thescentvault.ie">Contact us</a>.';
        case 'not_eligible':
          return 'That order doesn\'t include a Discovery Collection box. ' +
                 'Make sure you\'re using the order number from your Discovery Collection purchase, not a different order.';
        case 'cancelled':
        case 'refunded':
          return 'That order has been cancelled or refunded, so it can\'t be redeemed. ' +
                 'If you think that\'s a mistake,' + contact;
        case 'not_paid':
          return 'That order hasn\'t been marked as paid yet. Please try again once your payment has settled.';
        case 'window_closed':
          var extra = '';
          if (data.delivered_on) {
            extra = ' Your order was delivered on ' + escapeHtml(formatDate(data.delivered_on)) + '.';
          }
          return 'Your reward window has closed. Rewards must be redeemed within 90 days of delivery.' + extra +
                 ' Need help?' + contact;
        case 'rate_limited':
          return 'Too many attempts from this device. Please wait a few minutes and try again.';
        case 'invalid_input':
          return escapeHtml(data.message || 'Please check your inputs and try again.');
        case 'lookup_failed':
        case 'create_failed':
        case 'server_error':
        case 'unauthorized':
          return 'Something went wrong on our end. Refresh the page and try again. ' +
                 'If it keeps happening,' + contact;
        default:
          return escapeHtml(data.message ||
            'Something went wrong. Refresh the page and try again. If it keeps happening,') + contact;
      }
    }

    function renderSuccess(data, email) {
      root.classList.add('is-success');
      if (trust) trust.style.display = 'none';

      // Page stays collection-neutral on purpose — same look for every current
      // and future Discovery Collection. No per-collection tint applied.

      var rewards = data.rewards || [];
      var plural = rewards.length > 1;
      // Already-redeemed state (Task 7): worker returns status
      // 'existing_code_returned' OR any reward with reused === true.
      var isAlreadyRedeemed = data.status === 'existing_code_returned' ||
        rewards.some(function (r) { return r && r.reused; });

      // Title + sub change slightly depending on status
      var titleEl = root.querySelector('[data-tsv-success-title]');
      var subEl = root.querySelector('[data-tsv-success-sub]');
      if (isAlreadyRedeemed) {
        if (titleEl) titleEl.textContent = plural
          ? 'Your rewards were already redeemed'
          : 'Your reward was already redeemed';
        if (subEl) subEl.textContent = plural
          ? 'These are single-use codes. If you haven\'t applied them yet, paste them at checkout on any full-size bottle from your box.'
          : 'This is a single-use code. If you haven\'t applied it yet, paste it at checkout on any full-size bottle from your box.';
      } else {
        if (titleEl) titleEl.textContent = plural ? 'Your rewards are ready' : 'Your reward is ready';
        if (subEl) subEl.textContent = 'Use the single-use code' + (plural ? 's' : '') + ' below at checkout towards an eligible full-size bottle.';
      }

      // Verified confirmation card (Task 5). The worker currently returns
      // rewards[].collection (key like "for-him") and rewards[].label, plus
      // a top-level status. It does NOT return product handle, product title
      // or order date. We map collection key → product handle and use the
      // box image data-attributes baked in by Liquid.
      // TODO: worker could return order metadata { product_handle, order_date,
      // product_title } per reward to show a richer confirmation card.
      renderVerifiedCard(rewards[0] || {}, isAlreadyRedeemed);

      // Render the code cards
      var codesEl = root.querySelector('[data-tsv-codes]');
      if (codesEl) {
        codesEl.innerHTML = (data.rewards || []).map(function (r) {
          return '<div class="tsv-redeem__code-card">' +
                   '<div class="tsv-redeem__code-label">' + escapeHtml(r.label || r.collection || 'Reward') + '</div>' +
                   '<div class="tsv-redeem__code-value">' + escapeHtml(r.code) + '</div>' +
                   '<div class="tsv-redeem__code-meta">€10 OFF · EXPIRES ' + escapeHtml(formatDate(r.expires_at)).toUpperCase() + '</div>' +
                   '<button type="button" class="tsv-redeem__copy-btn" data-tsv-copy="' + escapeHtml(r.code) + '">Copy code</button>' +
                 '</div>';
        }).join('');

        // Wire up copy buttons
        codesEl.querySelectorAll('[data-tsv-copy]').forEach(function (btn) {
          btn.addEventListener('click', function () { copyCode(btn); });
        });
      }

      // Shop button — link to the worker-provided URL if present
      var shopBtn = root.querySelector('[data-tsv-shop-btn]');
      if (shopBtn) shopBtn.setAttribute('href', data.shop_full_bottles_url || shopUrl);

      // Footer note
      var footEl = root.querySelector('[data-tsv-success-foot]');
      if (footEl) footEl.textContent = 'We\'ve also emailed ' + (plural ? 'these codes' : 'this code') + ' to ' + email + '.';

      // Stash the FIRST code in localStorage so the discovery-bottles page
      // can pre-fill its reward banner when the customer goes to shop.
      try {
        var firstCode = (data.rewards && data.rewards[0] && data.rewards[0].code) || '';
        if (firstCode) {
          localStorage.setItem('tsv_reward_code', firstCode);
          localStorage.setItem('tsv_reward_code_meta', JSON.stringify({
            collection: data.rewards[0].collection || '',
            expires_at: data.rewards[0].expires_at || '',
            amount: 10,
            stored_at: new Date().toISOString()
          }));
        }
      } catch (_) {}

      // Scroll the panel into view on mobile
      try { successPanel.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (_) {}
    }

    function copyCode(btn) {
      var code = btn.getAttribute('data-tsv-copy');
      if (!code) return;
      var done = function () {
        var orig = btn.textContent;
        btn.classList.add('is-copied');
        btn.textContent = 'Copied';
        setTimeout(function () {
          btn.classList.remove('is-copied');
          btn.textContent = orig;
        }, 2000);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(done).catch(fallback);
      } else { fallback(); }
      function fallback() {
        try {
          var ta = document.createElement('textarea');
          ta.value = code;
          ta.style.position = 'fixed'; ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
          done();
        } catch (_) {}
      }
    }

    function setLoading(on) {
      root.classList.toggle('is-loading', !!on);
      btn.disabled = !!on;
      btn.textContent = on ? 'Verifying…' : (btn.getAttribute('data-orig-label') || btn.textContent);
      if (!on && btn.getAttribute('data-orig-label')) btn.textContent = btn.getAttribute('data-orig-label');
    }

    function showError(msgHtml) {
      root.classList.add('is-error');
      errorBox.innerHTML = msgHtml + ' <a href="#" data-tsv-retry>Try again</a>';
      errorBox.style.display = 'block';
      var retry = errorBox.querySelector('[data-tsv-retry]');
      if (retry) retry.addEventListener('click', function (e) { e.preventDefault(); hideError(); });
    }
    function hideError() {
      root.classList.remove('is-error');
      if (errorBox) { errorBox.innerHTML = ''; errorBox.style.display = 'none'; }
    }

    // Cache the original button label so loading state can restore it
    if (btn) btn.setAttribute('data-orig-label', btn.textContent.trim());

    // Run URL ?order=&email= auto-check now that the submit handler is wired.
    handleUrlParamPrefill(root);

    /* ─── Verified confirmation card (Task 5) ─── */
    function renderVerifiedCard(firstReward, isAlreadyRedeemed) {
      var card = root.querySelector('[data-tsv-redeem-success]');
      if (!card) return;

      var eyebrowEl = card.querySelector('[data-tsv-verified-eyebrow]');
      var titleEl = card.querySelector('[data-tsv-verified-title]');
      var subEl = card.querySelector('[data-tsv-verified-sub]');
      var pillEl = card.querySelector('[data-tsv-used-pill]');
      var imgWrap = card.querySelector('[data-tsv-verified-image-wrap]');

      if (eyebrowEl) eyebrowEl.textContent = isAlreadyRedeemed ? 'ALREADY UNLOCKED' : 'VERIFIED';
      if (pillEl) {
        if (isAlreadyRedeemed) { pillEl.hidden = false; pillEl.textContent = 'Already used'; }
        else { pillEl.hidden = true; }
      }

      // The worker returns `collection` (key like "for-him") and `label`
      // ("Discovery Collection: For Him"). We try the label first, then derive
      // from key.
      var label = firstReward && firstReward.label;
      var collectionKey = firstReward && firstReward.collection;
      var productHandle = collectionKey ? ('discovery-collection-' + collectionKey) : '';

      if (titleEl) {
        if (label) {
          // Convert "Discovery Collection: For Him" → "The Discovery Collection — For Him"
          // (no em dash in the brand spec, so we use a comma instead).
          titleEl.textContent = 'You bought ' + label.replace(/^Discovery Collection:\s*/i, 'The Discovery Collection, ');
        } else {
          titleEl.textContent = 'You bought The Discovery Collection';
        }
      }

      if (subEl) {
        // The worker doesn't return order_date — fall back to expires_at minus
        // 90 days as a rough ordered-on date (anchor is delivery date so this
        // is approximate). If neither is available, just hide the sub.
        var orderedOn = '';
        if (firstReward && firstReward.expires_at) {
          try {
            var d = new Date(firstReward.expires_at);
            if (!isNaN(d.getTime())) {
              d.setDate(d.getDate() - 90);
              orderedOn = formatDate(d.toISOString());
            }
          } catch (_) {}
        }
        if (isAlreadyRedeemed) {
          subEl.textContent = orderedOn ? ('Redeemed around ' + orderedOn) : '';
        } else {
          subEl.textContent = orderedOn ? ('Ordered around ' + orderedOn) : '';
        }
      }

      // Box image from data-attributes set by Liquid (Task 5).
      if (imgWrap && productHandle) {
        var imgUrl = card.getAttribute('data-img-' + collectionKey);
        if (imgUrl) {
          imgWrap.innerHTML = '<img class="tsv-redeem__verified-img" src="' +
            escapeHtml(imgUrl) + '" alt="' +
            escapeHtml(label || 'Discovery Collection box') + '">';
        }
      }

      card.classList.add('is-shown');
    }

    /* ─── Order help popover (Task 2) ─── */
    function initOrderHelpPopover(root) {
      var trigger = root.querySelector('[data-tsv-order-help-trigger]');
      var popover = root.querySelector('[data-tsv-order-help-popover]');
      var close = root.querySelector('[data-tsv-order-help-close]');
      if (!trigger || !popover) return;

      function setOpen(open) {
        popover.classList.toggle('is-open', !!open);
        trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      }
      trigger.addEventListener('click', function () {
        setOpen(!popover.classList.contains('is-open'));
      });
      if (close) close.addEventListener('click', function () { setOpen(false); });
      // Keyboard: ESC closes.
      popover.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { setOpen(false); trigger.focus(); }
      });
    }

    /* ─── Signed-in "Use a different account" (Task 3) ─── */
    function initSignedInToggle(root) {
      var link = root.querySelector('[data-tsv-use-different-account]');
      if (!link) return;
      link.addEventListener('click', function () {
        // Clear the order select (if present), make the email editable + empty,
        // and hide the signed-in banner. The customer can then type freely.
        var select = root.querySelector('[data-tsv-order-select]');
        if (select) {
          // Swap the select for a free-text input so manual entry works.
          var input = document.createElement('input');
          input.id = 'tsv-order-input';
          input.className = 'tsv-redeem__input';
          input.name = 'order_number';
          input.type = 'text';
          input.inputMode = 'text';
          input.autocomplete = 'off';
          input.placeholder = 'e.g. 551178 or #551178';
          input.required = true;
          select.parentNode.replaceChild(input, select);
        }
        var emailInput = root.querySelector('#tsv-email-input');
        if (emailInput) { emailInput.value = ''; emailInput.removeAttribute('readonly'); emailInput.focus(); }
        var banner = root.querySelector('[data-tsv-signed-in]');
        if (banner) banner.style.display = 'none';
      });
    }

    /* ─── How-redemption-works scroll reveal (Task 4) ─── */
    function initHowStrip(root) {
      var steps = root.querySelectorAll('[data-tsv-redeem-how-step]');
      if (!steps.length) return;
      var reduced = window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduced || !('IntersectionObserver' in window)) {
        steps.forEach(function (s) { s.classList.add('is-no-anim'); });
        return;
      }
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) {
            e.target.classList.add('is-revealed');
            io.unobserve(e.target);
          }
        });
      }, { threshold: 0.2, rootMargin: '0px 0px -10% 0px' });
      steps.forEach(function (s) { io.observe(s); });
    }

    /* ─── URL ?order=…&email=… auto-check (Task 7b) ─── */
    function handleUrlParamPrefill(root) {
      var params;
      try { params = new URLSearchParams(window.location.search); } catch (_) { return; }
      var orderParam = (params.get('order') || params.get('order_number') || '').trim();
      var emailParam = (params.get('email') || '').trim().toLowerCase();
      if (!orderParam || !emailParam) return;
      // Pre-fill whichever order field is present (input or select)
      var orderField = root.querySelector('#tsv-order-input');
      if (orderField) {
        if (orderField.tagName === 'SELECT') {
          // Try to find a matching option; if none, swap to a free-text input.
          var matched = false;
          Array.prototype.forEach.call(orderField.options, function (opt) {
            if (String(opt.value).replace(/^#/, '') === orderParam.replace(/^#/, '')) {
              opt.selected = true; matched = true;
            }
          });
          if (!matched) {
            var input = document.createElement('input');
            input.id = 'tsv-order-input';
            input.className = 'tsv-redeem__input';
            input.name = 'order_number';
            input.type = 'text';
            input.value = orderParam;
            orderField.parentNode.replaceChild(input, orderField);
          }
        } else {
          orderField.value = orderParam;
        }
      }
      var emailInput = root.querySelector('#tsv-email-input');
      if (emailInput) emailInput.value = emailParam;
      // Auto-submit so the user lands directly on the success / already-redeemed view.
      try { form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit', { cancelable: true })); } catch (_) {}
    }

    /* ─── Newsletter opt-in (Task 8) ─── */
    function postNewsletterOptIn(endpoint, email) {
      try {
        var params = new URLSearchParams();
        params.append('email', email);
        // Worker accepts an arbitrary `source` string and stores it on the row.
        // We use `redeem_page_opt_in` so admin reports can tell where the
        // signup came from.
        params.append('source', 'redeem_page_opt_in');
        fetch(endpoint, {
          method: 'POST',
          credentials: 'same-origin',
          cache: 'no-store',
          keepalive: true,
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: params.toString(),
        }).catch(function () { /* fire-and-forget */ });
      } catch (_) {}
    }
  } // end init()

  /* ─── Support form ("Having trouble?" panel) ─── */
  function initHelpPanel(root, helpEndpoint) {
    var trigger = root.querySelector('[data-tsv-help-trigger]');
    var panel = root.querySelector('[data-tsv-help-panel]');
    var form = root.querySelector('[data-tsv-help-form]');
    var submit = root.querySelector('[data-tsv-help-submit]');
    var result = root.querySelector('[data-tsv-help-result]');
    if (!trigger || !panel || !form || !submit || !result) return;

    var origLabel = submit.textContent.trim();

    trigger.addEventListener('click', function () {
      var open = root.classList.toggle('is-help-open');
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      trigger.textContent = open
        ? 'Hide contact form'
        : 'Having trouble? Tell us what happened →';
      if (open) {
        try { panel.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (_) {}
      }
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      clearResult();

      var data = new FormData(form);
      var name = String(data.get('name') || '').trim();
      var email = String(data.get('email') || '').trim().toLowerCase();
      var reason = String(data.get('reason') || '').trim();

      if (!name) return showResult('Please enter your name.', 'error');
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        return showResult('Please enter a valid email address so we can reply.', 'error');
      }
      if (!reason) return showResult('Please choose what the issue is.', 'error');

      setSubmitting(true);

      // Build URL-encoded body. Using GET via App Proxy avoids preflight issues.
      var params = new URLSearchParams();
      ['reason', 'name', 'email', 'order_number', 'postcode', 'message', 'website'].forEach(function (k) {
        var v = data.get(k);
        if (v != null) params.append(k, String(v));
      });

      fetch(helpEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      })
        .then(function (r) { return r.json().catch(function () { return null; }); })
        .then(function (resp) {
          setSubmitting(false);
          if (resp && resp.success) {
            showResult(
              resp.message ||
              'Thanks — your message has been received. We\'ll reply within 24 hours.',
              'success'
            );
            // Clear the inputs but keep the panel open so the user sees the message.
            try { form.reset(); } catch (_) {}
          } else {
            var msg = (resp && resp.message) ||
              'We couldn\'t send your message right now. Please try again in a minute.';
            showResult(msg, 'error');
          }
        })
        .catch(function () {
          setSubmitting(false);
          showResult(
            'We couldn\'t reach the reward service. Please check your connection and try again.',
            'error'
          );
        });
    });

    function setSubmitting(on) {
      submit.disabled = !!on;
      submit.textContent = on ? 'Sending…' : origLabel;
    }
    function showResult(text, kind) {
      result.className = 'tsv-redeem__help-result is-' + (kind === 'success' ? 'success' : 'error');
      result.textContent = text;
    }
    function clearResult() {
      result.className = 'tsv-redeem__help-result';
      result.textContent = '';
    }
  }

  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function formatDate(iso) {
    if (!iso) return '';
    try {
      var d = new Date(iso);
      if (isNaN(d.getTime())) return iso;
      return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric'
      }).format(d);
    } catch (_) { return iso; }
  }

  function boot() {
    document.querySelectorAll('[data-tsv-redeem]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init after Shopify section reloads in the customizer
  document.addEventListener('shopify:section:load', boot);
})();